import { activeCalendar, calendarEra, monthsInYear, weekdayNames } from './calendar.js';
import { isTimeMarkValue, type MarkRole } from './timeMarks.js';
import type { Calendar, TimePrecision } from './types.js';

// ---------------------------------------------------------------------------
// Zeitangaben im Text erkennen (PLAN.md §16): Vorschläge für Zeitmarken, die der Autor mit ✓ bestätigt. Deutsch; erkannt werden Datum
// („3. März 1823“, „03.03.1823“, „am 3. März“), Monat, Jahreszeit und Jahr („im März 1823“, „im Frühling 1823“, „im Jahr 1823“),
// Uhrzeiten („um acht Uhr“, „gegen halb neun“, „um 20:15“), Tageszeiten („am Abend“, „am Morgen des 3. März“) und relative Angaben
// („drei Tage später“, „nach zwei Wochen“, „am nächsten Morgen“, „in derselben Nacht“, „zwei Jahre zuvor“). Eine Uhrzeit direkt hinter
// einer Angabe gehört zu ihr („am nächsten Morgen um acht“). Dazu Enden („bis zum Abend“, „bis acht Uhr“, „bis zum 5. März“) und Dauern
// („drei Tage lang“, „für zwei Wochen“), PLAN.md §17.
// ---------------------------------------------------------------------------

export interface TimeSuggestion {
  /** Lage im untersuchten Text */
  from: number;
  to: number;
  text: string;
  /** Wert der Zeitmarke (siehe timeMarks.ts) */
  value: string;
  precision: TimePrecision | null;
  approx: boolean;
  /** Ende bzw. Dauer der Szene („bis zum Abend“, „drei Tage lang“); null: eine Zeit (die erste ist der Beginn) */
  role: MarkRole | null;
  /** Wort davor bzw. danach in derselben Zeile (leer: keins) – zum Merken einer abgelehnten Stelle wie bei den möglichen Verweisen */
  before: string;
  after: string;
}

const NUMBERS: Record<string, number> = {
  ein: 1, eine: 1, einen: 1, einem: 1, einer: 1, eins: 1, zwei: 2, drei: 3, vier: 4, fünf: 5, sechs: 6, sieben: 7, acht: 8, neun: 9,
  zehn: 10, elf: 11, zwölf: 12, zwanzig: 20, dreißig: 30, vierzig: 40, fünfzig: 50, hundert: 100,
};
const NUM = `(?:\\d{1,4}|${Object.keys(NUMBERS).sort((a, b) => b.length - a.length).join('|')})`;
const numOf = (s: string): number | null => (/^\d+$/.test(s) ? Number(s) : (NUMBERS[s.toLowerCase()] ?? null));

const UNIT = '(?:minuten?|stunden?|tag(?:e|en)?|wochen?|monat(?:e|en)?|jahr(?:e|en)?)';
const unitCode = (u: string): string => {
  const w = u.toLowerCase();
  return w.startsWith('min') ? 'min' : w.startsWith('stu') ? 'h' : w.startsWith('tag') ? 'd' : w.startsWith('woc') ? 'w' : w.startsWith('mon') ? 'mo' : 'y';
};

const GREGORIAN_MONTHS: Record<string, number> = {
  januar: 1, jänner: 1, februar: 2, feber: 2, märz: 3, april: 4, mai: 5, juni: 6, juli: 7, august: 8, september: 9, oktober: 10, november: 11, dezember: 12,
};
const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const alternatives = (names: readonly string[]): string => [...names].sort((a, b) => b.length - a.length).map(escapeRe).join('|');

/** Tageszeiten: ungefähre Uhrzeit */
const PARTS: Record<string, string> = { morgen: '07:00', vormittag: '10:00', mittag: '12:00', nachmittag: '15:00', abend: '19:00', nacht: '23:00' };
const PART = '(morgen|vormittag|mittag|nachmittag|abend|nacht)';
const DAY_OR_PART = '(tag|morgen|vormittag|mittag|nachmittag|abend)';

const SEASONS: Record<string, string> = { frühling: '03', frühjahr: '03', sommer: '06', herbst: '09', winter: '12' };

const B = '(?<![\\p{L}\\p{N}])';
const E = '(?![\\p{L}\\p{N}])';
const re = (body: string): RegExp => new RegExp(`${B}${body}${E}`, 'giu');
const pad = (n: number): string => String(n).padStart(2, '0');

/** Eine Uhrzeit direkt an `pos` (optional nach Komma und Leerraum): „um acht Uhr“, „gegen halb neun“, „um 20:15“, „kurz nach zehn abends“ */
const CLOCK = new RegExp(
  `(?:,?\\s*)(bis\\s+um|bis\\s+gegen|bis|um|gegen|etwa um|ungefähr um|kurz nach|kurz vor)\\s+(?:(\\d{1,2})[:.](\\d{2})(?:\\s*uhr)?|(\\d{1,2})\\s*uhr|halb\\s+(${NUM})|viertel\\s+(nach|vor)\\s+(${NUM})|(${NUM})(?:\\s+uhr)?)(?:\\s+(morgens|vormittags|mittags|nachmittags|abends|nachts))?${E}`,
  'iuy',
);
interface Clock {
  end: number;
  hh: number;
  mm: number;
  exact: boolean;
  approx: boolean;
  /** „bis acht Uhr“: ein Ende */
  until: boolean;
}
function clockAt(text: string, pos: number): Clock | null {
  CLOCK.lastIndex = pos;
  const m = CLOCK.exec(text);
  if (!m) return null;
  const lead = m[1]!.toLowerCase().replace(/\s+/g, ' ');
  let hh: number;
  let mm = 0;
  let exact = false;
  if (m[2] !== undefined) {
    hh = Number(m[2]);
    mm = Number(m[3]);
    exact = true;
  } else if (m[4] !== undefined) hh = Number(m[4]);
  else if (m[5] !== undefined) {
    hh = (numOf(m[5]) ?? 99) - 1;
    mm = 30;
    exact = true;
  } else if (m[6] !== undefined) {
    const n = numOf(m[7]!) ?? 99;
    hh = m[6].toLowerCase() === 'nach' ? n : n - 1;
    mm = m[6].toLowerCase() === 'nach' ? 15 : 45;
    exact = true;
  } else hh = numOf(m[8]!) ?? 99;
  const part = m[9]?.toLowerCase();
  if (part && hh < 12 && (part === 'nachmittags' || part === 'abends' || (part === 'nachts' && hh >= 6))) hh += 12;
  if (hh === 24) hh = 0;
  if (hh > 23 || mm > 59) return null;
  const until = lead.startsWith('bis');
  return { end: m.index + m[0].length, hh, mm, exact, approx: lead !== 'um' && lead !== 'bis' && lead !== 'bis um', until };
}

interface Found {
  from: number;
  to: number;
  value: string;
  precision: TimePrecision | null;
  approx: boolean;
  /** eine Uhrzeit dahinter gehört dazu */
  clock: 'date' | 'yearless' | 'rel' | null;
  role?: MarkRole | null;
}

/** Eine Tageszeit direkt hinter einem Abstand: „drei Tage später am Abend“, „tags darauf in der Nacht“ */
const PART_AFTER = new RegExp(`,?\\s+(?:(?:am|gegen)\\s+(morgen|vormittag|mittag|nachmittag|abend)|in\\s+der\\s+(nacht))${E}`, 'iuy');

/** Uhrzeit bzw. Tageszeit hinter einer Angabe anhängen („am nächsten Morgen um acht“ → +1d@08:00, „zwei Tage später am Abend“ → +2d@19:00) */
function withClock(text: string, f: Found): Found {
  if (!f.clock) return f;
  const c = clockAt(text, f.to);
  if (c && !c.until) {
    const at = `${pad(c.hh)}:${pad(c.mm)}`;
    const value = f.clock === 'date' ? `${f.value.slice(0, 10)}T${at}` : f.clock === 'yearless' ? `${f.value.slice(0, 7)}T${at}` : `${f.value.replace(/@.*$/, '')}@${at}`;
    return { ...f, to: c.end, value, precision: c.exact ? null : 'hour', approx: c.approx };
  }
  if (f.clock !== 'rel' || f.value.includes('@')) return f;
  PART_AFTER.lastIndex = f.to;
  const p = PART_AFTER.exec(text);
  return p ? { ...f, to: p.index + p[0].length, value: `${f.value}@${PARTS[(p[1] ?? p[2])!.toLowerCase()]}`, precision: 'hour', approx: true } : f;
}

type Rule = [RegExp, (m: RegExpExecArray) => Omit<Found, 'from' | 'to'> | null];

const rel = (sign: '+' | '-', n: number | null, unit: string, extra: Partial<Found> = {}): Omit<Found, 'from' | 'to'> | null =>
  n === null ? null : { value: `${sign}${n}${unitCode(unit)}`, precision: null, approx: false, clock: 'rel', ...extra };
const date = (d: string, mo: number, y: string | undefined): Omit<Found, 'from' | 'to'> =>
  y ? { value: `${y.padStart(4, '0')}-${pad(mo)}-${pad(Number(d))}`, precision: null, approx: false, clock: 'date' } : { value: `--${pad(mo)}-${pad(Number(d))}`, precision: null, approx: false, clock: 'yearless' };

/**
 * Die Regeln im Kalender der Geschichte (PF 16): Monatsnamen, Wochentage („am Montag, dem 3. März“) und Jahre („im Jahr 312 n. F.“)
 * des gregorianischen bzw. des eigenen Kalenders; Jahreszeiten nur im gregorianischen. Im eigenen Kalender hat ein Jahr hinter einem
 * Monat ein bis vier Ziffern, die Jahreszählung darf folgen, ein Monat steht auch im Genitiv („des Frostmonds“).
 */
function buildRules(cal: Calendar | null): Rule[] {
  const months: Record<string, number> = cal ? Object.fromEntries(cal.months.map((m, i) => [m.name.toLowerCase(), i + 1])) : GREGORIAN_MONTHS;
  const monthOf = (s: string): number => months[s.toLowerCase()]!;
  const MONTH = `(${alternatives(Object.keys(months))})${cal ? '(?:e?s)?' : ''}`;
  const era = calendarEra() ? `(?:\\s+${escapeRe(calendarEra())})?` : '';
  const YEAR = cal ? `(\\d{1,4})${era}` : '(\\d{4})';
  const YEAR_WORD = cal ? `(\\d{1,4})${era}` : '(\\d{3,4})';
  const weekdays = weekdayNames();
  const ON = `(?:am\\s+)?${weekdays.length > 0 ? `(?:(?:${alternatives(weekdays)}),?\\s+(?:(?:dem|den|der)\\s+)?)?` : ''}`;
  const n = monthsInYear();
  return [
    // relative Abstände
    [re(`(${NUM})\\s+(${UNIT})\\s+(?:später|danach|darauf)`), (m) => rel('+', numOf(m[1]!), m[2]!)],
    [re(`(${NUM})\\s+(${UNIT})\\s+(?:zuvor|vorher|früher)`), (m) => rel('-', numOf(m[1]!), m[2]!)],
    [re(`nach\\s+(${NUM})\\s+(${UNIT})`), (m) => rel('+', numOf(m[1]!), m[2]!)],
    // nächster bzw. derselbe Tag, mit Tageszeit
    [re(`(?:am|an dem)\\s+(?:nächsten|folgenden|darauffolgenden)\\s+${DAY_OR_PART}`), (m) => partRel(1, m[1]!)],
    [re(`(?:tags\\s+darauf|am\\s+tag\\s+darauf)`), () => rel('+', 1, 'tag')],
    [re(`(?:am|an dem)\\s+(?:selben|gleichen)\\s+${DAY_OR_PART}`), (m) => partRel(0, m[1]!)],
    [re(`in\\s+(?:derselben|der\\s+selben|der\\s+gleichen|der\\s+nächsten|der\\s+folgenden)\\s+nacht`), () => ({ value: '+0d@23:00', precision: 'hour', approx: true, clock: 'rel' })],
    // Datum: mit Tageszeit, mit Monatsnamen (auch mit Wochentag davor), als Zahlen
    [re(`(?:am\\s+)?${PART}\\s+des\\s+(\\d{1,2})\\.\\s*${MONTH}(?:\\s+${YEAR})?`), (m) => {
      const d = date(m[2]!, monthOf(m[3]!), m[4]);
      return { ...d, value: `${d.value}T${PARTS[m[1]!.toLowerCase()]}`, precision: 'hour', approx: true };
    }],
    [re(`${ON}(\\d{1,2})\\.\\s*${MONTH}(?:\\s+${YEAR})?`), (m) => date(m[1]!, monthOf(m[2]!), m[3])],
    [re(`${ON}(\\d{1,2})\\.(\\d{1,2})\\.(\\d{${cal ? '1,4' : '4'}})?`), (m) => (Number(m[2]) >= 1 && Number(m[2]) <= n ? date(m[1]!, Number(m[2]), m[3]) : null)],
    // Monat, Jahreszeit, Jahr
    [re(`(?:im\\s+)?${MONTH}\\s+${YEAR}`), (m) => ({ value: `${m[2]!.padStart(4, '0')}-${pad(monthOf(m[1]!))}`, precision: null, approx: false, clock: null })],
    ...(cal ? [] : ([[re(`(?:im\\s+)?(frühling|frühjahr|sommer|herbst|winter)\\s+(?:des\\s+jahres\\s+)?(\\d{4})`), (m) => ({ value: `${m[2]}-${SEASONS[m[1]!.toLowerCase()]}`, precision: null, approx: true, clock: null })]] as Rule[])),
    [re(`(?:im\\s+jahre?|anno)\\s+${YEAR_WORD}`), (m) => ({ value: m[1]!.padStart(4, '0'), precision: null, approx: false, clock: null })],
    // die Jahreszahl mit der Jahreszählung des eigenen Kalenders („312 n. F.“)
    ...(cal?.era ? ([[re(`(\\d{1,4})\\s+${escapeRe(cal.era)}`), (m) => ({ value: m[1]!.padStart(4, '0'), precision: null, approx: false, clock: null })]] as Rule[]) : []),
    // Enden: „bis zum nächsten Morgen“, „bis zum Abend“, „bis in die Nacht“, „bis zum 5. März“
    [re(`bis\\s+(?:zum|zur)\\s+(?:nächsten|folgenden)\\s+${DAY_OR_PART}`), (m) => ({ ...partRel(1, m[1]!), role: 'end' })],
    [re(`bis\\s+zum\\s+(morgen|vormittag|mittag|nachmittag|abend)`), (m) => ({ ...partRel(0, m[1]!), role: 'end' })],
    [re(`bis\\s+in\\s+die\\s+nacht`), () => ({ value: '+0d@23:00', precision: 'hour', approx: true, clock: 'rel', role: 'end' })],
    [re(`bis\\s+(?:zum\\s+)?(\\d{1,2})\\.\\s*${MONTH}(?:\\s+${YEAR})?`), (m) => ({ ...date(m[1]!, monthOf(m[2]!), m[3]), role: 'end' })],
    // Dauern: „drei Tage lang“, „für zwei Wochen“
    [re(`(?:für\\s+)?(${NUM})\\s+(${UNIT})\\s+lang`), (m) => duration(numOf(m[1]!), m[2]!)],
    [re(`für\\s+(${NUM})\\s+(${UNIT})`), (m) => duration(numOf(m[1]!), m[2]!)],
    // Tageszeit allein: an demselben Tag
    [re(`(?:am|gegen)\\s+(morgen|vormittag|mittag|nachmittag|abend)|in\\s+der\\s+(nacht)`), (m) => ({ value: `+0d@${PARTS[(m[1] ?? m[2])!.toLowerCase()]}`, precision: 'hour', approx: true, clock: 'rel' })],
  ];
}

let gregorianRules: Rule[] | null = null;
const rulesCache = new WeakMap<Calendar, Rule[]>();
/** Die Regeln des aktiven Kalenders (einmal gebaut) */
function rules(): Rule[] {
  const cal = activeCalendar();
  if (!cal) return (gregorianRules ??= buildRules(null));
  let r = rulesCache.get(cal);
  if (!r) rulesCache.set(cal, (r = buildRules(cal)));
  return r;
}

const duration = (n: number | null, unit: string): Omit<Found, 'from' | 'to'> | null =>
  n === null || n === 0 ? null : { value: `${n}${unitCode(unit)}`, precision: null, approx: false, clock: null, role: 'duration' };

function partRel(days: number, what: string): Omit<Found, 'from' | 'to'> {
  const w = what.toLowerCase();
  if (w === 'tag') return { value: `+${days}d`, precision: null, approx: false, clock: 'rel' };
  return { value: `+${days}d@${PARTS[w]}`, precision: 'hour', approx: true, clock: 'rel' };
}

/** Wort direkt vor bzw. hinter einer Stelle in derselben Zeile (leer: keins) */
function neighbours(text: string, from: number, to: number): { before: string; after: string } {
  const lineStart = text.lastIndexOf('\n', from - 1) + 1;
  const lineEnd = text.indexOf('\n', to) < 0 ? text.length : text.indexOf('\n', to);
  const before = /([\p{L}\p{N}]+)[^\p{L}\p{N}]*$/u.exec(text.slice(lineStart, from))?.[1] ?? '';
  const after = /^[^\p{L}\p{N}]*([\p{L}\p{N}]+)/u.exec(text.slice(to, lineEnd))?.[1] ?? '';
  return { before, after };
}

/**
 * Zeitangaben in einem Text (Klartext, z. B. ein Absatz): Vorschläge für Zeitmarken, nach Lage geordnet, ohne Überschneidungen (die
 * längere gewinnt). `skip`: Bereiche, die schon eine Zeitmarke tragen.
 */
export function findTimeSuggestions(text: string, skip: readonly [number, number][] = []): TimeSuggestion[] {
  const found: Found[] = [];
  for (const [rule, make] of rules()) {
    rule.lastIndex = 0;
    for (const m of text.matchAll(rule)) {
      const f = make(m);
      if (f) found.push(withClock(text, { ...f, from: m.index!, to: m.index! + m[0].length }));
    }
  }
  // eine Uhrzeit für sich: an demselben Tag
  for (const m of text.matchAll(/(?<![\p{L}\p{N}])(?=(?:bis|um|gegen|etwa um|ungefähr um|kurz nach|kurz vor)\s)/giu)) {
    const c = clockAt(text, m.index!);
    if (c) found.push({ from: m.index!, to: c.end, value: `+0d@${pad(c.hh)}:${pad(c.mm)}`, precision: c.exact ? null : 'hour', approx: c.approx, clock: null, role: c.until ? 'end' : null });
  }
  found.sort((a, b) => a.from - b.from || b.to - a.to);
  const out: TimeSuggestion[] = [];
  let last = -1;
  for (const f of found) {
    if (f.from < last || !isTimeMarkValue(f.value) || skip.some(([a, b]) => f.from < b && f.to > a)) continue;
    last = f.to;
    out.push({ from: f.from, to: f.to, text: text.slice(f.from, f.to), value: f.value, precision: f.precision, approx: f.approx, role: f.role ?? null, ...neighbours(text, f.from, f.to) });
  }
  return out;
}

/**
 * Eingabe einer Zeit von Hand (Feld beim Setzen einer Zeitmarke): ein Wert der Zeitmarken („+3d“, „1823-03-03T07:00“), ein Datum
 * („03.03.1823 07:00“) oder eine erkannte Angabe („drei Tage später“). null, wenn es keine Zeit ist.
 */
export function parseTimeInput(input: string): { value: string; precision: TimePrecision | null; approx: boolean; role: MarkRole | null } | null {
  const t = input.trim();
  if (isTimeMarkValue(t)) return { value: t, precision: null, approx: false, role: null };
  const d = /^(\d{1,2})\.(\d{1,2})\.(\d{1,4})(?:\s+(\d{1,2}):(\d{2}))?$/.exec(t);
  if (d) {
    const value = `${d[3]!.padStart(4, '0')}-${pad(Number(d[2]))}-${pad(Number(d[1]))}${d[4] !== undefined ? `T${pad(Number(d[4]))}:${d[5]}` : ''}`;
    return isTimeMarkValue(value) ? { value, precision: null, approx: false, role: null } : null;
  }
  const s = findTimeSuggestions(t);
  return s.length === 1 && s[0]!.from === 0 && s[0]!.to === t.length ? { value: s[0]!.value, precision: s[0]!.precision, approx: s[0]!.approx, role: s[0]!.role } : null;
}
